import { Circle, Document, Font, Image, Page, Path, pdf, Rect, StyleSheet, Svg, Text, View } from '@react-pdf/renderer'
import { useEffect } from 'react'

import EuclidBold from '../../Styles/pdfFonts/euclidBold.ttf'
import EuclidItalic from '../../Styles/pdfFonts/euclidItalic.ttf'
import EuclidRegular from '../../Styles/pdfFonts/euclidRegular.ttf'

import { translate as translateAreas } from '../../i18n/Library/areasCorrelation.js'
import { translate as translatePosition } from '../../i18n/Library/hierarchicalLevel'
import { translate as translateSubareas } from '../../i18n/Library/subareasTranslate'

const UpArrowSvg = () => (
	<Svg width='10' height='10' viewBox='0 0 24 24'>
		<Path d='M12 2 L18 14 H6 Z' fill='#6366F1' />
	</Svg>
)

const RobotSvg = () => (
	<Svg width='10' height='10' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
		<Rect x='5' y='7' width='14' height='10' rx='2' fill='black' />
		<Circle cx='9' cy='12' r='1.5' fill='white' />
		<Circle cx='15' cy='12' r='1.5' fill='white' />
		<Rect x='3' y='9' width='2' height='6' fill='black' />
		<Rect x='19' y='9' width='2' height='6' fill='black' />
		<Rect x='11.5' y='2' width='1' height='3' fill='black' />
	</Svg>
)

const SuccessionPlanPDFSpanish = ({ employeeList, planName, lists, selectedCountry, currentDate }) => (
	<Document>
		<Page style={[styles.page, { padding: 0 }]}>
			<View style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
				<Image src='https://assets.mydna.company/pdf-sucession-plan/cover.jpg' cache style={{ height: '110%' }} />
				<View style={styles.roundedBox}>
					<Text style={styles.headerText}>REPORTE</Text>
					<Text style={styles.title}>
						Plan de{'\n'}
						Sucesión
					</Text>
					<Text style={styles.name}>{planName}</Text>
					<Text style={styles.description}>
						Este reporte es un producto de MyDNA. El Plan de Sucesión MyDNA proporciona apoyo en la identificación de talentos clave, el desarrollo de
						habilidades esenciales y la creación de estrategias de transición eficientes para garantizar la continuidad y el crecimiento de la
						empresa.
					</Text>
					<Text style={styles.footer}>2022 | C Todos los derechos reservados.</Text>
				</View>
			</View>
		</Page>

		<Page style={[styles.page, { padding: 0 }]}>
			<Image src='https://assets.mydna.company/pdf-sucession-plan/Mandalas.jpg' cache style={{ height: '110%' }} />
		</Page>

		<Page style={styles.page}>
			<View style={[styles.roundedTextBox, { marginTop: 20 }]}>
				<Text style={styles.text}>Plan de Sucesión</Text>
			</View>

			<View style={{ flexDirection: 'row', justifyContent: 'flex-start', marginTop: 80, fontSize: 9 }}>
				<Text style={{ marginRight: 20 }}>Nombre: {planName}</Text>
				<Text style={{ marginRight: 20 }}>Fecha: {currentDate}</Text>
				<Text style={{ marginRight: 20 }}>País: {selectedCountry}</Text>
			</View>

			<View style={[styles.table, { marginTop: 20 }]}>
				<View style={[styles.tableRow, styles.tableHeader]}>
					<Text style={[styles.tableCell, styles.rankCell]}>Rank</Text>
					<Text style={[styles.tableCell, styles.nameCell]}>Colaborador y Cargo</Text>
					<Text style={[styles.tableCell, styles.evaluationCell]}>Evaluación</Text>
					<Text style={[styles.tableCell, styles.nextStepCell]}>Próximo Paso</Text>
					<Text style={[styles.tableCell, styles.scoreCell]}>Puntuación</Text>
				</View>

				{employeeList?.map((employee, index) => {
					const performance = Math.min(100, Math.floor(employee?.performance))
					const potential = Math.min(100, Math.floor(employee?.potential))
					const nextSteps = employee?.next_steps_hr.slice(0, 3)

					return (
						<View key={employee.employee_id} style={styles.tableRow}>
							<Text style={[styles.tableCell, styles.rankCell]}>{index + 1}</Text>

							<View style={[styles.tableCell, styles.nameCell]}>
								<Text>{employee?.name}</Text>
								<Text>{employee?.jobname}</Text>
							</View>
							<View style={[styles.tableCell, styles.evaluationCell]}>
								<Text>Desempeño: {performance}</Text>
								<Text>Potencial: {potential}</Text>
							</View>
							<View style={[styles.tableCell, styles.nextStepCell]}>
								{nextSteps.length === 0 ? (
									<Text style={{ color: '#6366F1' }}>No hay un próximo paso definido</Text>
								) : (
									nextSteps.map((step, idx) => {
										const nextLevel = lists?.levels?.find(level => String(level.id) === String(step?.level_id))
										const nextSubarea = lists?.subareas?.find(subarea => String(subarea.id) === String(step?.subarea_id))
										return (
											<View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
												<UpArrowSvg />
												<Text key={idx} style={{ marginLeft: 1 }}>
													{nextLevel?.value} de {nextSubarea?.value}
												</Text>
											</View>
										)
									})
								)}
							</View>
							<View style={[styles.tableCell, styles.scoreCell]}>
								<Text>{employee?.algorithm_score?.toFixed()} %</Text>
							</View>
						</View>
					)
				})}
			</View>

			<View style={styles.footerPage}>
				<Text style={styles.pageNumber} render={({ pageNumber }) => `${pageNumber}`} fixed />

				<View style={styles.logoContainer}>
					<Image src='https://assets.mydna.company/mydnalogo.png' style={[styles.logo, { marginRight: 5 }]} />
				</View>
			</View>
		</Page>
	</Document>
)

const SuccessionPlanPDFPortuguese = ({ employeeList, planName, lists, selectedCountry, currentDate, language }) => (
	<Document>
		<Page style={[styles.page, { padding: 0 }]}>
			<View style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
				<Image src='https://assets.mydna.company/pdf-sucession-plan/cover.jpg' cache style={{ height: '110%' }} />
				<View style={styles.roundedBox}>
					<Text style={styles.headerText}>RELATÓRIO</Text>
					<Text style={styles.title}>
						Plano de{'\n'}
						Sucessão
					</Text>
					<Text style={styles.name}>{planName}</Text>
					<Text style={styles.description}>
						Este relatório é um produto da MyDNA. O Plano de Sucessão MyDNA oferece suporte na identificação de talentos-chave, no desenvolvimento de
						habilidades essenciais e na criação de estratégias de transição eficientes para garantir a continuidade e o crescimento da empresa.
					</Text>
					<Text style={styles.footer}>2022 | C Todos os direitos reservados.</Text>
				</View>
			</View>
		</Page>

		<Page style={[styles.page, { padding: 0 }]}>
			<Image src='https://assets.mydna.company/pdf-sucession-plan/Mandalas.jpg' cache style={{ height: '110%' }} />
		</Page>

		<Page style={styles.page}>
			<View style={[styles.roundedTextBox, { marginTop: 20 }]}>
				<Text style={styles.text}>Plano de Sucessão</Text>
			</View>

			<View style={{ flexDirection: 'row', justifyContent: 'flex-start', marginTop: 80, fontSize: 9 }}>
				<Text style={{ marginRight: 20 }}>Nome: {planName}</Text>
				<Text style={{ marginRight: 20 }}>Data: {currentDate}</Text>
				<Text style={{ marginRight: 20 }}>País: {selectedCountry}</Text>
			</View>

			<View style={[styles.table, { marginTop: 20 }]}>
				<View style={[styles.tableRow, styles.tableHeader]}>
					<Text style={[styles.tableCell, styles.rankCell]}>Rank</Text>
					<Text style={[styles.tableCell, styles.nameCell]}>Colaborador e Cargo</Text>
					<Text style={[styles.tableCell, styles.evaluationCell]}>Avaliação</Text>
					<Text style={[styles.tableCell, styles.nextStepCell]}>Próximo Passo</Text>
					<Text style={[styles.tableCell, styles.scoreCell]}>Pontuação</Text>
				</View>

				{employeeList?.map((employee, index) => {
					const performance = Math.min(100, Math.floor(employee?.performance))
					const potential = Math.min(100, Math.floor(employee?.potential))
					const nextSteps = employee?.next_steps_hr.slice(0, 3)

					return (
						<View key={employee.employee_id} style={styles.tableRow}>
							<Text style={[styles.tableCell, styles.rankCell]}>{index + 1}</Text>

							<View style={[styles.tableCell, styles.nameCell]}>
								<Text>{employee?.name}</Text>
								<Text>{employee?.jobname}</Text>
							</View>
							<View style={[styles.tableCell, styles.evaluationCell]}>
								<Text>Desempenho: {performance}</Text>
								<Text>Potencial: {potential}</Text>
							</View>
							<View style={[styles.tableCell, styles.nextStepCell]}>
								{nextSteps.length === 0 ? (
									<Text style={{ color: '#6366F1' }}>Não há um próximo passo definido</Text>
								) : (
									nextSteps.map((step, idx) => {
										const nextLevel = lists?.levels?.find(level => String(level.id) === String(step?.level_id))
										const nextSubarea = lists?.subareas?.find(subarea => String(subarea.id) === String(step?.subarea_id))
										const translatedSubareas = translateSubareas[language][nextSubarea?.value]
										const translatedLevel = translatePosition[language][nextLevel?.value]
										return (
											<View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
												<UpArrowSvg />
												<Text key={idx} style={{ marginLeft: 1 }}>
													{translatedLevel} de {translatedSubareas}
												</Text>
											</View>
										)
									})
								)}
							</View>
							<View style={[styles.tableCell, styles.scoreCell]}>
								<Text>{employee?.algorithm_score?.toFixed()} %</Text>
							</View>
						</View>
					)
				})}
			</View>

			<View style={styles.footerPage}>
				<Text style={styles.pageNumber} render={({ pageNumber }) => `${pageNumber}`} fixed />

				<View style={styles.logoContainer}>
					<Image src='https://assets.mydna.company/mydnalogo.png' style={[styles.logo, { marginRight: 5 }]} />
				</View>
			</View>
		</Page>
	</Document>
)

Font.register({
	family: 'Euclid Circular B',
	fonts: [
		{
			src: EuclidRegular,
			fontWeight: 'normal',
		},
		{
			src: EuclidBold,
			fontWeight: 'bold',
		},
		{
			src: EuclidItalic,
			fontStyle: 'italic',
		},
	],
})

const styles = StyleSheet.create({
	page: {
		position: 'relative',
		backgroundColor: '#FFFFFF',
		width: '100%',
		padding: 10,
	},
	backgroundImage: {
		position: 'absolute',
		width: '100%',
		height: '100%',
	},
	roundedBox: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		width: '80%',
		backgroundColor: '#7c6cec',
		borderTopRightRadius: 20,
		padding: 20,
	},
	headerText: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'normal',
		color: '#FFFFFF',
		fontSize: 12,
		marginBottom: 22,
		marginLeft: 10,
	},
	title: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'bold',
		color: '#FFFFFF',
		fontWeight: 900,
		fontSize: 42,
		marginBottom: 22,
		marginLeft: 10,
	},
	name: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'normal',
		color: '#FFFFFF',
		fontSize: 18,
		marginBottom: 22,
		marginLeft: 10,
	},
	description: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'normal',
		color: '#FFFFFF',
		fontSize: 10,
		marginBottom: 22,
		marginLeft: 10,
		lineHeight: 1.5,
	},
	defaultText: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'normal',
		color: 'black',
		fontSize: 10,
		marginBottom: 14,
		marginHorizontal: 26,
		lineHeight: 1.5,
	},
	footer: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'normal',
		color: '#FFFFFF',
		fontSize: 10,
		marginBottom: 22,
		marginLeft: 10,
	},

	//Tag for TitlePages
	roundedTextBox: {
		position: 'absolute',
		width: 'auto',
		// maxWidth: 350,
		left: 0,
		padding: 10,
		backgroundColor: '#7c6cec',
		borderTopRightRadius: 35,
		borderBottomRightRadius: 35,
		marginBottom: 20,
	},
	text: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'bold',
		color: '#FFFFFF',
		fontSize: 18,
		marginLeft: 10,
		marginRight: 10,
		fontWeight: 700,
	},

	table: {
		display: 'table',
		width: 'auto',
	},
	tableRow: {
		flexDirection: 'row',
		borderBottom: '1px solid #E5E7EB',
		paddingVertical: 5,
		alignItems: 'center',
	},
	tableHeader: {
		backgroundColor: '#F3F4F6',
		fontWeight: 'bold',
	},
	tableCell: {
		flex: 1,
		padding: 5,
		fontSize: 9,
	},
	rankCell: {
		flex: 0.5,
		textAlign: 'center',
	},
	nameCell: {
		flex: 3,
		fontWeight: 'bold',
	},
	scoreCell: {
		flex: 1,
		textAlign: 'center',
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
	},
	evaluationCell: {
		flex: 2,
	},
	nextStepCell: {
		flex: 2,
	},

	//ContainerDimensions
	container: {
		marginLeft: 20,
		marginRight: 20,
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: '#0078D4',
		padding: 10,
		borderRadius: 10,
	},
	iconContainer: {
		paddingLeft: 19,
		width: 20,
		height: 20,
		borderRadius: 20,
		backgroundColor: '#FFFFFF',
		justifyContent: 'center',
		alignItems: 'center',
		marginRight: 4,
	},
	textContainer: {
		display: 'flex',
	},
	defaultTextContainer: {
		fontFamily: 'Euclid Circular B',
		fontWeight: 'normal',
		color: 'black',
		fontSize: 10,
	},

	pill: {
		margin: 2,
		padding: 4,
		backgroundColor: '#008ACD',
		color: '#fff',
		borderRadius: 20,
		fontSize: 10,
		flexBasis: 'auto',
		width: 'auto',
	},

	progressBarContainer: {
		marginLeft: 20,
		marginRight: 20,
		width: '100%',
		backgroundColor: '#E0F3FC',
		borderRadius: 12,
		height: 12,
		overflow: 'hidden',
		marginBottom: 10,
	},
	progressBarFill: {
		backgroundColor: '#008ACD',
		height: '100%',
		borderRadius: 12,
	},

	containerSpecs: {
		display: 'flex',
		flexDirection: 'row',
		border: '1px solid #E0E0E0',
		borderRadius: 8,
		marginBottom: 10,
		overflow: 'hidden',
		width: '100%',
	},
	leftBox: {
		backgroundColor: '#008ACD',
		padding: 10,
		width: '30%',
		color: 'white',
	},
	rightBox: {
		padding: 10,
		width: '70%',
	},
	listText: {
		fontSize: 10,
		marginBottom: 3,
	},

	layoutContainer: {
		padding: 10,
		border: '1px solid #E0E0E0',
		borderRadius: 8,
		width: '100%',
	},
	layoutHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		backgroundColor: '#008ACD',
		padding: 10,
		color: 'white',
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
	},
	layoutTitle: {
		fontSize: 14,
		fontWeight: 'bold',
	},
	layoutPercentage: {
		fontSize: 14,
		fontWeight: 'bold',
	},
	layoutContent: {
		padding: 10,
		borderBottom: 8,
	},
	layoutRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 5,
	},
	layoutColumn: {
		width: '45%',
	},
	layoutSubtitle: {
		fontSize: 12,
		fontWeight: 'bold',
		marginBottom: 5,
	},
	layoutParagraph: {
		fontSize: 10,
		marginBottom: 5,
	},
	layoutBadge: {
		backgroundColor: '#E3F2FD',
		color: '#007BFF',
		fontSize: 10,
		padding: '2px 4px',
		borderRadius: 8,
		alignSelf: 'flex-start',
	},

	circleNumber: {
		width: 16,
		height: 16,
		borderRadius: 15,
		backgroundColor: '#f1f4f9',
		justifyContent: 'center',
		alignItems: 'center',
		marginRight: 10,
	},

	footerPage: {
		position: 'absolute',
		bottom: 10,
		left: 0,
		right: 0,
		height: 50,
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingHorizontal: 30,
	},
	pageNumber: {
		fontSize: 10,
		color: '#000',
	},
	logoContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	logo: {
		width: 60,
		height: 'auto',
	},

	containerOptions: {
		backgroundColor: '#f5f8fa',
		borderRadius: 10,
		padding: 10,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: 5,
		marginBottom: 20,
		marginLeft: 20,
		marginRight: 20,
	},
	option: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	circle: {
		width: 10,
		height: 10,
		borderRadius: 5,
		borderWidth: 1,
		borderColor: '#E69138',
		marginRight: 4,
	},
})

const PDFButtonSuccessionPlan = ({ employeeList, planName, countryName, language, filters, lists, shouldGeneratePDF, onPDFGenerated }) => {
	const currentDate = new Date().toLocaleDateString()

	const handleOpenPDF = async () => {
		const companyId = JSON.parse(localStorage.getItem('user')).id

		const doc = (
			<>
				{language === 'pt' ? (
					<SuccessionPlanPDFPortuguese
						employeeList={employeeList}
						lists={lists}
						planName={planName}
						currentDate={currentDate}
						selectedCountry={countryName ?? '-'}
						language={'pt'}
					/>
				) : (
					<SuccessionPlanPDFSpanish
						employeeList={employeeList}
						lists={lists}
						planName={planName}
						currentDate={currentDate}
						selectedCountry={countryName ?? '-'}
					/>
				)}
			</>
		)

		const asPdf = pdf([])
		asPdf.updateContainer(doc)

		//Generating PDF and Binary
		const blob = await asPdf.toBlob()
		const arrayBuffer = await blob.arrayBuffer()

		// const sendData = {
		// 	'filename': planName,
		// 	'file': arrayBuffer,
		// 	'company_id': companyId,
		// 	'country_id': filters.country || 0,
		// }

		// try {
		// 	await successionPlan.saveActualPlan(sendData)
		// } catch (error) {
		// 	console.log('Error while saving plan')
		// }

		const url = URL.createObjectURL(blob)
		window.open(url, '_blank')

		onPDFGenerated()
	}

	useEffect(() => {
		if (shouldGeneratePDF) {
			handleOpenPDF()
		}
	}, [shouldGeneratePDF, employeeList, lists])

	return null
}
export default PDFButtonSuccessionPlan
